import { useState } from 'react';
import cx from 'clsx';
import { Checkbox, Tag, useToast } from '@kcd/design-system';

import { ShoebillImage } from '~/components/Image';
import { SHOPPING_CARD_ITEM_STATUS_MESSAGE } from '~/domains/cart/constants';
import ProductSalesTargetBadge from '~/domains/products/components/ProductSalesTargetBadge';
import { useLinkComponent } from '~/hooks/mobileApp';
import type { OAISchema } from '~/types/openapi';
import { openDialogAsync } from '~/utils/mobileApp';

import CartCounter from '../CartCounter';
import ExtraHelpText from '../ExtraHelpText';

import { CloseIcon } from '~/assets/icons';

const CartCard = ({
  item,
  border,
  visibleCheckbox = true,
  onChangeCheckbox,
  onDeleteClick,
  onCountChange,
  className,
}: {
  border?: boolean;
  item: OAISchema['MarketShoppingCartItemResponseV2'];
  visibleCheckbox?: boolean;
  onChangeCheckbox?: (isSelected: boolean) => void;
  onDeleteClick?: () => void;
  onCountChange?: (quantity: number) => void;
  className?: string;
}) => {
  const LinkComponent = useLinkComponent();
  const [quantity, setQuantity] = useState(item.quantity || 0);
  const toast = useToast();
  const 유효한상품 = item.status === 'NORMAL';
  const isFirstOrderProduct = item.salesTargets?.includes('FIRST_ORDER');

  return (
    <div
      className={cx(
        'py-16',
        { 'border-b border-screen-02': border },
        className,
      )}
    >
      <div className="relative flex items-start">
        {visibleCheckbox && (
          <Checkbox
            disabled={!유효한상품}
            checked={유효한상품 ? item.isSelected! : false}
            onChange={(event) => onChangeCheckbox?.(event.target.checked)}
          />
        )}
        <LinkComponent
          href={`/vendors/${item.vendorId!}/products/${item.offerId!}`}
          className="flex flex-1 items-start"
        >
          <div className="relative mx-8 size-[58px] overflow-hidden rounded-12 border border-gray-300">
            <ShoebillImage
              alt="product"
              width={64}
              height={64}
              objectFit="contain"
              src={item.productImageUrl!}
            />
          </div>
          <div className="flex-1">
            {!유효한상품 && (
              <div className="mb-4 flex items-center gap-4">
                <Tag className="bg-gray-600 text-gray-000" label="구매불가" />
                <span className="text-disabled typo-caption100-regular">
                  {SHOPPING_CARD_ITEM_STATUS_MESSAGE[item.status || 'NORMAL']}
                </span>
              </div>
            )}
            <div className="w-[calc(100vw-200px)] max-w-[600px]">
              {isFirstOrderProduct && (
                <ProductSalesTargetBadge salesTargets={item.salesTargets} />
              )}
              <h3 className="block truncate typo-paragraph100-regular">
                {item.productName}
              </h3>
              {item.optionName && (
                <span className="line-clamp-2 text-description typo-footnote300-regular">
                  {item.optionName}
                </span>
              )}
            </div>
          </div>
        </LinkComponent>
        <CloseIcon
          className="absolute right-0 top-0 size-16 shrink-0"
          onClick={() => {
            void openDialogAsync({
              type: 'destructive',
              description: '상품을 삭제하시겠습니까?',
            }).then(({ confirmed }) => {
              if (!confirmed) {
                return;
              }

              onDeleteClick?.();
              toast('상품이 삭제되었습니다', { offset: 100 });
            });
          }}
        />
      </div>

      <div className="mt-16 flex flex-col items-end">
        <ExtraHelpText {...item} />
        <div className="mt-4 flex items-center">
          <span className="mr-12 typo-paragraph300-semibold">
            {item.price
              ? `${(item.price * quantity).toLocaleString()}원`
              : '상담신청 상품'}
          </span>
          <CartCounter
            item={item}
            disabled={!유효한상품}
            onChange={(changedQuantity) => {
              setQuantity(changedQuantity);
              onCountChange?.(changedQuantity);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CartCard;
